import { submitManagerNotes } from '../actions/managerNotesActions';
import { State } from '../redux-state';
import breakpoints from '../styles/breakpoints';
import Button from './Button';
import Question from './Question';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

interface Option {
    id: number;
    name: string;
}

interface QuestionObj {
    id: number;
    name: string;
    type: string;
    isMandatory: boolean;
    options: Array<Option>;
}

interface ManagerNotesAnswer {
    questionId: number;
    optionId?: number | null;
    notes?: string | null;
}

interface ManagerNotesQuestionProps {
    questions: Array<QuestionObj>;
    answers: Array<ManagerNotesAnswer>;
}

interface NextStepContainerProps {
    leftSize: number;
}

const NextStepContainer = styled.div<NextStepContainerProps>`
    display: flex;
    align-items: center;
    text-decoration: none;
    margin: 1em 0 1em 0;
    justify-content: flex-end;
    ${breakpoints.onlyMobile} {
        justify-content: center;
    }
`;

const ManagerNotesQuestion: React.FC<ManagerNotesQuestionProps> = ({
    questions,
    answers,
}) => {
    const { survey_id, uid } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation(['ManagerNotes']);
    const leftSize = 55;

    let emailAuth: any;
    const stateVar = location.state;
    if (stateVar && 'emailAuth' in stateVar) {
        emailAuth = stateVar.emailAuth;
    }

    const managerUid = sessionStorage.getItem('managerUid') || '';

    const answersState = useSelector(
        (state: State): any => state.managerNotesState?.answers || [],
    );

    // Local state mirrors FeedbackQuestion — uses feedbackOptionId/feedback shape
    // so the existing Question component can be reused without modification.
    const [notesAnswers, setNotesAnswers] = useState<Array<any>>([]);
    const [buttonDisabled, setButtonDisabled] = useState<boolean>(false);

    useEffect(() => {
        setNotesAnswers(
            questions.map((question: QuestionObj, index: number) => {
                const saved = answersState.find(
                    (a: ManagerNotesAnswer) => a.questionId === question.id,
                ) || answersState[index];
                return {
                    feedbackOptionId: saved?.optionId ?? null,
                    feedbackQuestionId: question.id,
                    feedback: saved?.notes ?? '',
                    type: question.type,
                    isMandatory: question.isMandatory,
                };
            }),
        );
    }, [questions, answersState]);

    useEffect(() => {
        setButtonDisabled(
            notesAnswers.some((a) => a.isMandatory && !a.feedback),
        );
    }, [notesAnswers]);

    const setAnswersObject = (answer: any, questionId: number) => {
        const target = notesAnswers.find(
            (a) => a.feedbackQuestionId === questionId,
        );
        if (target) {
            target.feedbackOptionId = answer.id;
            target.feedback = answer.feedback;
            setNotesAnswers([...notesAnswers]);
        }
    };

    const onContinue = () => {
        // Map back to manager notes shape before submitting
        const payload = notesAnswers.map((a) => ({
            questionId: a.feedbackQuestionId,
            optionId: a.feedbackOptionId ?? null,
            notes: a.feedback ?? null,
        }));

        dispatch(submitManagerNotes(payload, survey_id, uid, managerUid) as any);
        navigate(`/${survey_id}/${managerUid}/reportees`, {
            state: { emailAuth },
            replace: true,
        });
    };

    return (
        <React.Fragment>
            {questions.map((question, index) => (
                <Question
                    name={question.name}
                    type={question.type}
                    key={question.id}
                    counter={index + 1}
                    currentAnswer={notesAnswers[index]}
                    options={question.options}
                    hideIndex={true}
                    placeholder="Please provide any specific domain knowledge that should be noted."
                    autoGrow={true}
                    setCurrentAnswer={(answer: any) =>
                        setAnswersObject(answer, question.id)
                    }
                />
            ))}

            <NextStepContainer leftSize={leftSize}>
                {buttonDisabled ? (
                    <Button disabled={buttonDisabled}>
                        {t('submitFeedback')}
                    </Button>
                ) : (
                    <Button onClick={onContinue}>
                        {t('submitFeedback')}
                    </Button>
                )}
            </NextStepContainer>
        </React.Fragment>
    );
};

export default ManagerNotesQuestion;
